import React from 'react';
import ServicePageLayout from '../../components/seo/ServicePageLayout';
import SubServiceGrid from '../../components/seo/SubServiceGrid';
import { getGallery } from '../../seo/galleryConfig';

const acSubServices = [
  { label: 'Air Conditioning Installation', blurb: 'Split systems, multi-splits, VRF and cassette installs for homes and offices.' },
  { label: 'Air Conditioning Servicing', blurb: 'Annual servicing to maintain efficiency, warranty cover and clean air output.' },
  { label: 'Air Conditioning Maintenance', blurb: 'Planned maintenance contracts for offices, retail, gyms and small commercial.' },
  { label: 'Air Conditioning Repairs', blurb: 'Diagnosis and repair of common AC faults — coolant, fans, control boards.' },
  { label: 'Emergency AC Callouts', blurb: 'Out-of-hours response when a critical system fails — call our 24/7 line.' },
];

const faqs = [
  {
    q: 'Do you install both home and office air conditioning?',
    a: 'Yes. We install single-split units for single rooms and home offices, multi-split systems for whole-home or whole-office cooling, and small VRF systems for larger commercial sites. We will quote on what suits your usage, building, and budget.'
  },
  {
    q: 'How often should air conditioning be serviced?',
    a: 'Annually for domestic systems, twice yearly for heavily-used commercial systems. Regular servicing keeps efficiency high, extends equipment life, maintains manufacturer warranty cover, and ensures the air being circulated is clean.'
  },
  {
    q: 'My AC unit is making a strange noise — what should I do?',
    a: 'Switch it off and book a diagnostic visit. New noises are usually a sign of a worn fan motor, loose blade, or compressor issue — all of which get worse fast if the unit keeps running. Catching it now is usually a much cheaper repair than waiting.'
  },
  {
    q: 'Do you offer emergency AC repair callouts?',
    a: 'Yes — particularly for commercial customers where a failed system means lost business (restaurants, gyms, server rooms, retail). Call our 24/7 line and we will dispatch the nearest engineer with the most common parts on board.'
  },
  {
    q: 'Are your AC engineers F-Gas certified?',
    a: 'Yes — all our refrigerant work is carried out by F-Gas certified engineers as required by UK law for any system containing fluorinated greenhouse gases.'
  },
];

const relatedServices = [
  { slug: 'refrigeration', label: 'Refrigeration', blurb: 'Commercial and domestic refrigeration servicing and repairs.' },
  { slug: 'property-maintenance', label: 'Property Maintenance', blurb: 'Bundle AC servicing into a wider property care contract.' },
  { slug: 'emergency-plumber', label: 'Emergency Plumber', blurb: 'Other 24/7 callout services.' },
];

const AirConditioningPage = () => (
  <ServicePageLayout
    seoTitle="Air Conditioning Services in Kent, London & Essex | H.A.R.P Solutions LTD"
    seoDescription="Air conditioning installation, servicing, maintenance and repair across Kent, London and Essex. F-Gas certified engineers. 24/7 emergency response. Call +44 330 122 1094."
    canonicalPath="/services/air-conditioning"
    keywords="air conditioning, AC installation, AC servicing, AC repair, air conditioning Kent, air conditioning London, air conditioning Essex"
    pageTitle="Air Conditioning Services for Kent, London & Essex"
    serviceName="Air Conditioning Services"
    serviceSlug="air-conditioning"
    heroSubheading="Installation, servicing, maintenance and emergency response — F-Gas certified."
    intro="H.A.R.P Solutions installs, services and repairs air conditioning systems for homes, offices and small commercial premises across Kent, London and Essex. All refrigerant work is carried out by F-Gas certified engineers, every install is documented for warranty compliance, and we offer planned maintenance contracts that keep your system running at peak efficiency."
    bullets={[
      'F-Gas certified engineers',
      'Split, multi-split, cassette and VRF systems',
      'Annual servicing keeps warranties valid',
      'Planned maintenance for commercial sites',
      'Emergency callouts for critical systems',
      'Domestic and light-commercial coverage',
    ]}
    faqs={faqs}
    relatedServices={relatedServices}
    galleryImages={getGallery('air-conditioning')}
  >
    <h2>Air Conditioning, Done Properly</h2>
    <p>
      A well-installed and well-maintained air conditioning system is one of the best long-term investments you can make in a home or
      commercial space. Conversely, a badly-installed one will run inefficiently, leak refrigerant, fail prematurely and cost a fortune in
      energy bills. We focus on getting both the install and the ongoing care right — so your system delivers what the manufacturer
      promised, for as long as the manufacturer promised.
    </p>

    <h2>Our Air Conditioning Sub-Services</h2>
    <SubServiceGrid
      title="Choose A Service Below"
      subtitle="Each item is a service we provide. Call us to discuss your specific situation and we'll quote accordingly."
      items={acSubServices}
    />

    <h2 className="!mt-12">What Makes A Good Air Conditioning Engineer?</h2>
    <p>
      Three things make a real difference:
    </p>
    <ul>
      <li><strong>F-Gas certification.</strong> Required by UK law for any refrigerant handling. Anyone working on the gas circuit without it is breaking the law and their work may invalidate your insurance.</li>
      <li><strong>Sizing the system properly.</strong> Most underperforming AC systems are oversized OR undersized for the room. Proper heat-load calculation before specifying the unit avoids years of compromise.</li>
      <li><strong>Quality install.</strong> Pipework runs cleanly, condensate drainage is correctly fallen, electrical isolation is properly fitted. These are the things you don't see — but they are why one engineer's install lasts 15 years and another's leaks after 3.</li>
    </ul>

    <h2>Domestic Air Conditioning</h2>
    <p>
      For homes we typically install wall-mounted split systems — discreet, quiet, and offering both cooling in summer and supplemental
      heating in winter (heat-pump models). Bedrooms, home offices, conservatories and lofts are the most common rooms we cool. We can
      also install multi-split systems where a single outdoor unit serves 2-5 indoor units for whole-home coverage.
    </p>

    <h2>Commercial Air Conditioning</h2>
    <p>
      Restaurants, gyms, offices, salons, retail shops and small commercial premises all rely on consistent climate control. We install
      cassette and ducted systems for clean ceiling-integrated cooling, and offer planned-maintenance contracts that include scheduled
      servicing, priority emergency response, and consolidated monthly billing. Many of our commercial customers value the no-disruption
      Saturday or out-of-hours servicing slots.
    </p>

    <h2>Servicing &amp; Maintenance</h2>
    <p>
      Annual servicing is the single biggest factor in long-term reliability. A service typically includes: coolant pressure check,
      filter clean or replacement, coil clean, condensate drain test, electrical safety check, and full operational test across all
      modes. Most servicing visits take 60-90 minutes per indoor unit. We'll book in your next service automatically so you never miss one.
    </p>

    <h2>Book A Quote</h2>
    <p>
      Call <a href="tel:+443301221094"><strong>+44 330 122 1094</strong></a> for a free site survey and quote, or use the form below. For
      urgent AC failures, call us to check current availability.
    </p>
  </ServicePageLayout>
);

export default AirConditioningPage;
